import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { useMutation, useQuery } from "react-query";
import classes from "./PermissionMatrix.module.scss";
import { UserPermissionsAPI } from "../../../../constants/API";
import { commonHeaders } from "../../../../constants/HeadersAPI";
import { queryClient } from "../../../../queryClient";

export type PermissionDto = {
  permissionKey: string;
  canView: boolean;
  canEdit: boolean;
};

export type PermissionRow = PermissionDto;

const SECTION_KEYS = {
  BRANCHES: "BRANCHES",
  SERVICES: "SERVICES",
  EMPLOYEES: "EMPLOYEES",
  WORKPLACES: "WORKPLACES",
  ANALYTICS: "ANALYTICS",
  SETTINGS: "SETTINGS",
  USER_AREA: "USER_AREA",
} as const;

const ACTION_KEYS = {
  CREATE: "CREATE",
  VIEW: "VIEW",
  EDIT: "EDIT",
  DELETE: "DELETE",
  ACTIVITY: "ACTIVITY",
} as const;

type SectionId = keyof typeof SECTION_KEYS;
type ActionId = keyof typeof ACTION_KEYS;

type SectionConfig = {
  id: SectionId;
  label: string;
  actions: ActionId[];
};

const sections: SectionConfig[] = [
  {
    id: "BRANCHES",
    label: "Филиалы",
    actions: ["VIEW", "EDIT"],
  },
  {
    id: "SERVICES",
    label: "Категории и услуги",
    actions: ["CREATE", "VIEW", "EDIT", "DELETE", "ACTIVITY"],
  },
  {
    id: "EMPLOYEES",
    label: "Сотрудники",
    actions: ["CREATE", "VIEW", "EDIT", "DELETE", "ACTIVITY"],
  },
  {
    id: "WORKPLACES",
    label: "Рабочие места",
    actions: ["CREATE", "VIEW", "EDIT", "DELETE", "ACTIVITY"],
  },
  {
    id: "ANALYTICS",
    label: "Аналитика и отчёты",
    actions: ["VIEW"],
  },
  {
    id: "SETTINGS",
    label: "Настройки",
    actions: ["VIEW", "EDIT"],
  },
  {
    id: "USER_AREA",
    label: "Личный кабинет",
    actions: ["VIEW", "EDIT"],
  },
];

const ACTION_LABEL: Record<ActionId, string> = {
  CREATE: "Создание",
  VIEW: "Просмотр",
  EDIT: "Изменение",
  DELETE: "Удаление",
  ACTIVITY: "Активность",
};

const buildPermissionKey = (section: SectionId, action: ActionId): string => {
  switch (section) {
    case "BRANCHES":
      return action === "VIEW" ? "BRANCHES_VIEW" : "BRANCHES_EDIT";
    case "SERVICES":
      return `SERVICES_${action}`;
    case "EMPLOYEES":
      return `EMPLOYEES_${action}`;
    case "WORKPLACES":
      return `WORKPLACES_${action}`;
    case "ANALYTICS":
      return "ANALYTICS_VIEW";
    case "SETTINGS":
      return action === "VIEW" ? "SETTINGS_VIEW" : "SETTINGS_EDIT";
    case "USER_AREA":
      return action === "VIEW" ? "USER_AREA_VIEW" : "USER_AREA_EDIT";
    default:
      return "";
  }
};

// Раздел "Филиалы" сейчас управляется доступом по роли/authority, а не матрицей прав.
// Поэтому "Просмотр" для филиалов в матрице не используем.
// "Изменение" оставляем как управляемое право.
const LOCKED_PERMISSION_KEYS = new Set<string>(["BRANCHES_VIEW"]);

const allPermissionKeys: string[] = sections.flatMap((s) =>
  s.actions.map((a) => buildPermissionKey(s.id, a)),
);
const selectablePermissionKeys = allPermissionKeys.filter(
  (k) => !LOCKED_PERMISSION_KEYS.has(k),
);

type PermissionMatrixProps =
  | { userId: number; value?: never; onChange?: never }
  | {
      userId?: undefined;
      value: PermissionRow[];
      onChange: (rows: PermissionRow[]) => void;
    };

export const PermissionMatrix: FC<PermissionMatrixProps> = (props) => {
  const { userId, value: controlledValue, onChange: controlledOnChange } = props;
  const isControlled = userId == null;
  const [internalRows, setInternalRows] = useState<PermissionRow[]>([]);

  const rows = isControlled ? controlledValue : internalRows;
  const setRows = isControlled
    ? (
        next:
          | PermissionRow[]
          | ((prev: PermissionRow[] | undefined) => PermissionRow[]),
      ) => {
        const prev = controlledValue;
        const nextRows =
          typeof next === "function" ? next(prev) : (next as PermissionRow[]);
        controlledOnChange(nextRows);
      }
    : setInternalRows;

  const ensureAllKeysPresent = (source: PermissionDto[] | undefined) => {
    const byKey = new Map(
      (source || []).map((p) => [p.permissionKey, { canView: !!p.canView }]),
    );
    return allPermissionKeys.map<PermissionRow>((key) => {
      const existing = byKey.get(key);
      const canView = existing?.canView ?? false;
      return {
        permissionKey: key,
        canView,
        // В матрице каждый permissionKey уже "атомарный" (например USER_AREA_EDIT).
        // Поэтому "наличие права" храним в canView, а canEdit не используем.
        // Иначе при одном "Просмотр" можно случайно отправить canEdit=true и бэкенд посчитает это edit-доступом.
        canEdit: false,
      };
    });
  };

  const { isFetching } = useQuery(
    ["userPermissions", userId],
    () =>
      axios.get(
        UserPermissionsAPI.getPermissionsForUser(userId!),
        commonHeaders(),
      ) as Promise<AxiosResponse<PermissionDto[]>>,
    {
      enabled: !!userId,
      onSuccess: (resp) => {
        setInternalRows(ensureAllKeysPresent(resp.data));
      },
    },
  );

  const { mutate: save, isLoading: isSaving } = useMutation(
    ["saveUserPermissions", userId],
    (payload: { permissions: PermissionDto[] }) =>
      axios.put(
        UserPermissionsAPI.putPermissionsForUser(userId),
        payload,
        commonHeaders(),
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["userPermissions", userId]);
        queryClient.invalidateQueries("myPermissions");
      },
    },
  );

  const toViewKeyIfImplied = (permissionKey: string): string | null => {
    const m = permissionKey.match(
      /^(SERVICES|EMPLOYEES|WORKPLACES)_(CREATE|EDIT|DELETE|ACTIVITY)$/,
    );
    if (m) return `${m[1]}_VIEW`;
    if (permissionKey === "SETTINGS_EDIT") return "SETTINGS_VIEW";
    if (permissionKey === "USER_AREA_EDIT") return "USER_AREA_VIEW";
    if (permissionKey === "BRANCHES_EDIT") return "BRANCHES_VIEW";
    return null;
  };

  const updatePermission = (permissionKey: string, canView: boolean) => {
    if (LOCKED_PERMISSION_KEYS.has(permissionKey)) return;
    setRows((prevRows) => {
      const prev = prevRows || [];
      const impliedViewKey =
        canView === true ? toViewKeyIfImplied(permissionKey) : null;
      const existing = prev.find((r) => r.permissionKey === permissionKey);

      const allKeysSet = new Set(allPermissionKeys);
      const byKey = new Map(prev.map((r) => [r.permissionKey, r]));

      if (!existing) {
        byKey.set(permissionKey, { permissionKey, canView, canEdit: false });
      } else {
        byKey.set(permissionKey, { ...existing, canView, canEdit: false });
      }

      if (
        impliedViewKey &&
        allKeysSet.has(impliedViewKey) &&
        !LOCKED_PERMISSION_KEYS.has(impliedViewKey)
      ) {
        const existingView = byKey.get(impliedViewKey);
        if (existingView) {
          byKey.set(impliedViewKey, {
            ...existingView,
            canView: true,
            canEdit: false,
          });
        } else {
          byKey.set(impliedViewKey, {
            permissionKey: impliedViewKey,
            canView: true,
            canEdit: false,
          });
        }
      }

      return Array.from(byKey.values()).filter((r) =>
        allKeysSet.has(r.permissionKey),
      );
    });
  };

  const setAll = (value: boolean) => {
    setRows(
      allPermissionKeys.map<PermissionRow>((key) => ({
        permissionKey: key,
        canView: LOCKED_PERMISSION_KEYS.has(key) ? false : value,
        canEdit: false,
      })),
    );
  };

  const sectionState = useMemo(() => {
    const map = new Map<
      SectionId,
      { total: number; enabled: number; keys: string[] }
    >();
    sections.forEach((s) => {
      const keys = s.actions
        .map((a) => buildPermissionKey(s.id, a))
        .filter((k) => !LOCKED_PERMISSION_KEYS.has(k));
      const enabled = keys.filter((k) =>
        rows?.some((r) => r.permissionKey === k && r.canView),
      ).length;
      map.set(s.id, { total: keys.length, enabled, keys });
    });
    return map;
  }, [rows]);

  const canViewKeys = useMemo(() => {
    return new Set((rows || []).filter((r) => r.canView).map((r) => r.permissionKey));
  }, [rows]);

  const selectedSelectableCount = selectablePermissionKeys.filter((k) =>
    canViewKeys.has(k),
  ).length;

  const allEnabled =
    rows != null &&
    selectablePermissionKeys.length > 0 &&
    selectedSelectableCount === selectablePermissionKeys.length;

  const isSelectAllIndeterminate =
    rows != null &&
    selectedSelectableCount > 0 &&
    selectedSelectableCount < selectablePermissionKeys.length;

  const selectAllRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSelectAllIndeterminate;
    }
  }, [isSelectAllIndeterminate]);

  const isEmpty = useMemo(() => !rows || rows.length === 0, [rows]);

  useEffect(() => {
    if (isControlled && (!rows || rows.length === 0)) {
      setRows(
        allPermissionKeys.map<PermissionRow>((key) => ({
          permissionKey: key,
          canView: false,
          canEdit: false,
        })),
      );
    }
  }, [isControlled]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.title}>Матрица прав</div>
        {!isControlled && (
          <button
            type="button"
            className={classes.saveBtn}
            onClick={() =>
              (() => {
                const payload = {
                  permissions: (rows || []).map((r) => ({
                    permissionKey: r.permissionKey,
                    canView: r.canView,
                    canEdit: r.canEdit,
                  })),
                };

                save(payload);
              })()
            }
            disabled={isSaving || isFetching || isEmpty}
          >
            Сохранить права
          </button>
        )}
      </div>

      <div className={classes.selectAllRow}>
        <label className={classes.selectAllLabel}>
          <input
            ref={selectAllRef}
            type="checkbox"
            checked={allEnabled}
            onChange={(e) => setAll(e.target.checked)}
          />
          <span>Выбрать всё</span>
        </label>
      </div>

      <div className={classes.table}>
        <div className={classes.tableHeader}>
          <div className={classes.colMenu}>Раздел</div>
          {(["CREATE", "VIEW", "EDIT", "DELETE", "ACTIVITY"] as ActionId[]).map(
            (action) => (
              <div key={action} className={classes.colCheck}>
                {ACTION_LABEL[action]}
              </div>
            ),
          )}
        </div>

        {sections.map((section) => {
          const state = sectionState.get(section.id)!;
          const sectionChecked = state.enabled === state.total;

          return (
            <div key={section.id} className={classes.tableRow}>
              <div className={classes.colMenu}>
                {section.id === "BRANCHES" ? (
                  <label className={classes.sectionLabel}>
                    <input
                      type="checkbox"
                      checked={sectionChecked}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            state.enabled > 0 && state.enabled < state.total;
                        }
                      }}
                      onChange={(e) => {
                        const next = e.target.checked;
                        const sectionKeys = state.keys;
                        setRows((prevRows) => {
                          const prev = prevRows || [];
                          const allKeysSet = new Set(allPermissionKeys);
                          const byKey = new Map(
                            prev.map((r) => [r.permissionKey, r]),
                          );

                          sectionKeys.forEach((key) => {
                            if (!allKeysSet.has(key)) return;
                            if (LOCKED_PERMISSION_KEYS.has(key)) return;
                            const existing = byKey.get(key);
                            if (existing) {
                              byKey.set(key, {
                                ...existing,
                                canView: next,
                                canEdit: false,
                              });
                            } else {
                              byKey.set(key, {
                                permissionKey: key,
                                canView: next,
                                canEdit: false,
                              });
                            }
                          });

                          return Array.from(byKey.values()).filter((r) =>
                            allKeysSet.has(r.permissionKey),
                          );
                        });
                      }}
                    />
                    <span>{section.label}</span>
                  </label>
                ) : (
                  <label className={classes.sectionLabel}>
                    <input
                      type="checkbox"
                      checked={sectionChecked}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            state.enabled > 0 && state.enabled < state.total;
                        }
                      }}
                      onChange={(e) => {
                        const next = e.target.checked;
                        const sectionKeys = state.keys;
                        setRows((prevRows) => {
                          const prev = prevRows || [];
                          const allKeysSet = new Set(allPermissionKeys);
                          const byKey = new Map(
                            prev.map((r) => [r.permissionKey, r]),
                          );

                          sectionKeys.forEach((key) => {
                            if (!allKeysSet.has(key)) return;
                            if (LOCKED_PERMISSION_KEYS.has(key)) return;
                            const existing = byKey.get(key);
                            if (existing) {
                              byKey.set(key, {
                                ...existing,
                                canView: next,
                                canEdit: false,
                              });
                            } else {
                              byKey.set(key, {
                                permissionKey: key,
                                canView: next,
                                canEdit: false,
                              });
                            }
                          });

                          return Array.from(byKey.values()).filter((r) =>
                            allKeysSet.has(r.permissionKey),
                          );
                        });
                      }}
                    />
                    <span>{section.label}</span>
                  </label>
                )}
              </div>

              {(["CREATE", "VIEW", "EDIT", "DELETE", "ACTIVITY"] as ActionId[]).map(
                (action) => {
                  const key = buildPermissionKey(section.id, action);
                  if (!section.actions.includes(action)) {
                    return (
                      <div key={action} className={classes.colCheck}>
                        -
                      </div>
                    );
                  }
                  if (LOCKED_PERMISSION_KEYS.has(key)) {
                    return (
                      <div key={action} className={classes.colCheck}>
                        -
                      </div>
                    );
                  }
                  const checked = !!rows?.some(
                    (r) => r.permissionKey === key && r.canView,
                  );
                  return (
                    <div key={action} className={classes.colCheck}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          updatePermission(key, e.target.checked)
                        }
                      />
                    </div>
                  );
                },
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

